/**
 * Command-Center control-plane client (`/api/v1/*` + `/agent/v1/*`).
 *
 * The trading data-api client lives in `./client.ts` (Zod-validated `/v2/*`).
 * This client talks to the CC Fastify server instead: cookie-JWT
 * authenticated, fleet management endpoints. Shapes mirror the CC server
 * routes + the V3.1 fleet schema (`server/src/db/migrations/1001_fleet.sql`,
 * V3.1/docs/ARCHITECTURE.md §4).
 *
 * Ported from the legacy axios client (`web/src-cc-legacy/api/client.ts`)
 * onto plain `fetch` for consistency with the V3 client.
 */

export class FleetApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function req<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (!res.ok) throw new FleetApiError(res.status, `${res.status} ${res.statusText}`);
      throw new FleetApiError(res.status, "non-JSON response from CC server");
    }
  }
  if (!res.ok) {
    const msg =
      (data as { error?: string; message?: string } | null)?.error ??
      (data as { message?: string } | null)?.message ??
      `${res.status} ${res.statusText}`;
    throw new FleetApiError(res.status, msg);
  }
  return data as T;
}

const get = <T>(path: string) => req<T>("GET", path);
const post = <T>(path: string, body?: unknown) => req<T>("POST", path, body);
const patch = <T>(path: string, body?: unknown) => req<T>("PATCH", path, body);
const put = <T>(path: string, body?: unknown) => req<T>("PUT", path, body);
const del = <T>(path: string) => req<T>("DELETE", path);

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type BotStatus = "enrolling" | "online" | "offline" | "paused" | "disabled";

export interface Bot {
  id: string;
  display_name: string;
  status: BotStatus;
  current_config_id: string | null;
  last_seen_at: string | null;
  enrolled_at: string;
  metadata: Record<string, unknown>;
}

export interface BotStat {
  id: string;
  agent_id: string;
  ts: string;
  open_orders: number;
  todays_pnl_usdc: string;
  /** Server-derived all-time PnL (USDC), continuous across daily resets. */
  cumulative_pnl_usdc: string;
  errors_1m: number;
  mode: string;
  current_config_id: string | null;
}

export interface Command {
  id: string;
  agent_id: string;
  verb: string;
  status: string;
  created_at: string;
  acked_at: string | null;
  ack_ok: boolean | null;
  ack_error: string | null;
  payload: Record<string, unknown>;
}

export interface BotPauseResult {
  ok: boolean;
  /** Effective full pause after the change (true on resume = something broader still pauses the bot). */
  paused: boolean;
  still_paused_by: Array<{ scope_kind: string; scope_id: string; reason: string | null }>;
}

export interface CcConfig {
  id: string;
  label: string | null;
}

/** `exec.health` snapshot journaled by the server (ARCHITECTURE.md §3). */
export interface AgentHealth {
  agent_id?: string;
  user_ws_connected: boolean;
  wallet_usdc?: string | null;
  allowance_usdc?: string | null;
  paused_markets: string[];
  open_orders: number;
  mode: string;
  ts?: string;
}

/** Row of `agent_market_assignments` (+ optional server-side enrichment). */
export interface Assignment {
  agent_id: string;
  market_id: string;
  active_config_version_id: string | null;
  paused: boolean;
  mode: "paper" | "live";
  activated_at: string | null;
  applies_from_window_start: string | null;
  created_at?: string;
  updated_at?: string;
  /** Optional enrichment when the server joins agents. */
  agent_display_name?: string;
  agent_status?: BotStatus;
}

/** Row of `agent_actions` — flat per-bot action journal. */
export interface AgentAction {
  id: number | string;
  agent_id: string;
  market_id: string | null;
  kind: string;
  ref_id: string | null;
  payload: Record<string, unknown> | null;
  ts: string;
}

export interface AgentActionPage {
  items: AgentAction[];
  /** Legacy key emitted by CC servers before the `items` contract. */
  actions?: AgentAction[];
  next_cursor?: string | null;
  has_more?: boolean;
}

/**
 * Flatten journal pages for rendering. Tolerates servers that still emit the
 * page under `actions`, and never lets a missing key become a `[undefined]`
 * element (Array.prototype.flatMap only flattens arrays).
 */
export function flattenActionPages(pages: AgentActionPage[] | undefined): AgentAction[] {
  return (pages ?? []).flatMap((p) => {
    const rows = Array.isArray(p?.items) ? p.items : Array.isArray(p?.actions) ? p.actions : [];
    return rows.filter((a): a is AgentAction => a != null && typeof a === "object");
  });
}

export type SignalVerb = "place" | "sell" | "settle" | "manage_pending" | "prewarm";
export type DeliveryStatus = "queued" | "sent" | "acked" | "rejected" | "expired" | "failed";

/** `signal_deliveries` row. */
export interface SignalDelivery {
  signal_id: string;
  agent_id: string;
  status: DeliveryStatus;
  ok: boolean | null;
  result: Record<string, unknown> | null;
  error: string | null;
  latency_ms: number | null;
  mismatch: Record<string, unknown> | null;
  sent_at: string | null;
  acked_at: string | null;
  agent_display_name?: string;
}

/** `signals` row joined with its per-agent deliveries. */
export interface SignalRow {
  signal_id: string;
  market_id: string;
  slug: string;
  verb: SignalVerb;
  config_version_id: string | null;
  payload?: Record<string, unknown>;
  deadline_at: string | null;
  created_at: string;
  deliveries: SignalDelivery[];
}

export interface SignalPage {
  /** Server field name (GET /api/v1/fleet/signals). */
  signals?: SignalRow[];
  /** Legacy field name; tolerated for older servers. */
  items?: SignalRow[];
  next_cursor?: string | null;
  has_more?: boolean;
}

/** Trading config version exposed by the fleet config catalogue. */
export interface TradingConfigVersion {
  config_version_id: string;
  market_id?: string | null;
  created_at?: string;
  notes?: string | null;
  is_active?: boolean;
  config_json?: string | Record<string, unknown>;
}

export interface AuditEntry {
  id: string;
  actor: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  ip: string | null;
  ts: string;
}

export interface Channel {
  id: string;
  label: string;
  type: string;
  enabled: boolean;
  config: Record<string, unknown>;
}

export interface Rule {
  id: string;
  label: string;
  enabled: boolean;
  event_kind: string | null;
  channel_ids: string[];
  message_template: string;
  cooldown_seconds: number;
}

export interface NotificationRow {
  id: string;
  rule_id: string | null;
  agent_id: string | null;
  channel_id: string | null;
  status: "sent" | "failed" | "suppressed";
  message: string;
  error: string | null;
  sent_at: string;
}

export interface Operator {
  id: string;
  email: string;
  is_admin: boolean;
}

/** First-login / MFA state machine returned by /auth/login, /auth/me, /auth/setup. */
export type AuthStep = "complete_setup" | "enroll_mfa" | "mfa" | "ready";

export interface UserRow {
  id: string;
  email: string;
  display_name: string | null;
  role: "admin" | "viewer";
  status: "active" | "disabled";
  must_change_password: boolean;
  profile_completed: boolean;
  mfa_enabled: boolean;
  auth_revision: number;
  created_at: string;
  last_login_at: string | null;
  grants: string[] | "all";
}

export interface MeResponse {
  user: Omit<UserRow, "grants">;
  next: AuthStep;
  grants: string[] | "all";
  operator: Operator;
}

/** SSE event names emitted on /api/v1/stream (shared/src/protocol.ts). */
export type SseEventType =
  | "bot.status"
  | "bot.stats"
  | "bot.event"
  | "command.ack"
  | "config.activated"
  | "notification.fired"
  | "signal.emitted"
  | "signal.result"
  | "agent.health"
  | "assignment.changed";

// ─── Client ──────────────────────────────────────────────────────────────────

export const fleetApi = {
  auth: {
    login: (email: string, password: string) =>
      post<{ ok: boolean; next: AuthStep; user: MeResponse["user"] }>("/api/v1/auth/login", { email, password }),
    me: () => get<MeResponse>("/api/v1/auth/me"),
    logout: () => post<{ ok: boolean }>("/api/v1/auth/logout"),
    setup: (body: { new_password: string; display_name: string }) =>
      post<{ ok: boolean; next: AuthStep }>("/api/v1/auth/setup", body),
    changePassword: (body: { current_password: string; new_password: string }) =>
      post<{ ok: boolean }>("/api/v1/auth/change-password", body),
    mfaEnroll: () => post<{ ok: boolean; otpauth: string; secret: string }>("/api/v1/auth/mfa/enroll"),
    mfaActivate: (code: string) =>
      post<{ ok: boolean; next: AuthStep; recovery_codes: string[] }>("/api/v1/auth/mfa/activate", { code }),
    mfaVerify: (code: string) => post<{ ok: boolean; next: AuthStep }>("/api/v1/auth/mfa/verify", { code }),
  },

  users: {
    list: () => get<UserRow[]>("/api/v1/users"),
    create: (body: { email: string; role: "admin" | "viewer"; temp_password: string; display_name?: string; grants?: string[] }) =>
      post<UserRow>("/api/v1/users", body),
    update: (id: string, body: { role?: "admin" | "viewer"; status?: "active" | "disabled"; display_name?: string; grants?: string[] }) =>
      patch<UserRow>(`/api/v1/users/${encodeURIComponent(id)}`, body),
    resetPassword: (id: string, body: { temp_password: string; reason?: string }) =>
      post<{ ok: boolean }>(`/api/v1/users/${encodeURIComponent(id)}/reset-password`, body),
    mfaRecovery: (id: string, reason: string) =>
      post<{ ok: boolean }>(`/api/v1/users/${encodeURIComponent(id)}/mfa-recovery`, { reason, confirm: true }),
  },

  agents: {
    list: () => get<Bot[]>("/api/v1/agents"),
    get: (id: string) => get<Bot>(`/api/v1/agents/${encodeURIComponent(id)}`),
    stats: (id: string, q: { limit?: number; since?: string } = {}) =>
      get<BotStat[]>(`/api/v1/agents/${encodeURIComponent(id)}/stats${qs(q)}`),
    commands: (id: string) =>
      get<Command[]>(`/api/v1/agents/${encodeURIComponent(id)}/commands`),
    update: (id: string, body: { display_name?: string; metadata?: Record<string, unknown> }) =>
      patch<Bot>(`/api/v1/agents/${encodeURIComponent(id)}`, body),
    disable: (id: string) => del<{ ok: boolean }>(`/api/v1/agents/${encodeURIComponent(id)}`),
  },

  commands: {
    issue: (body: {
      agent_id: string;
      verb: string;
      payload?: Record<string, unknown>;
      ttl_seconds?: number;
    }) => post<Command>("/api/v1/commands", body),
    /**
     * Poll the bot's command list until `cmdId` is acknowledged (ok or
     * refused) or `timeoutMs` passes. The POST response is returned the
     * moment the frame is written to the bot's WebSocket, so it can never
     * carry the ack; this is how the UI learns what the Pi actually did.
     */
    waitForAck: async (agentId: string, cmdId: string, timeoutMs = 10_000): Promise<Command | null> => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1_000));
        const cmds = await get<Command[]>(`/api/v1/agents/${encodeURIComponent(agentId)}/commands`).catch(() => [] as Command[]);
        const cmd = cmds.find((c) => c.id === cmdId);
        if (cmd && (cmd.acked_at || cmd.status === "failed" || cmd.status === "expired")) return cmd;
      }
      return null;
    },
  },

  configs: {
    /** Legacy CC bot-level configs (verb `apply_config`). */
    list: () => get<CcConfig[]>("/api/v1/configs"),
  },

  enrollment: {
    createCode: () =>
      post<{ code: string; expires_at: string }>("/agent/v1/enrollment-codes"),
  },

  audit: {
    list: (q: { limit?: number; actor?: string } = {}) =>
      get<AuditEntry[]>(`/api/v1/audit${qs(q)}`),
  },

  notifications: {
    list: () => get<NotificationRow[]>("/api/v1/notifications"),
    channels: {
      list: () => get<Channel[]>("/api/v1/notifications/channels"),
      create: (body: { label: string; type: string; config: Record<string, unknown> }) =>
        post<Channel>("/api/v1/notifications/channels", body),
      update: (id: string, body: { enabled?: boolean }) =>
        patch<Channel>(`/api/v1/notifications/channels/${encodeURIComponent(id)}`, body),
      remove: (id: string) =>
        del<{ ok: boolean }>(`/api/v1/notifications/channels/${encodeURIComponent(id)}`),
    },
    rules: {
      list: () => get<Rule[]>("/api/v1/notifications/rules"),
      create: (body: {
        label: string;
        event_kind: string | null;
        channel_ids: string[];
        message_template: string;
        cooldown_seconds: number;
      }) => post<Rule>("/api/v1/notifications/rules", body),
      update: (id: string, body: { enabled?: boolean }) =>
        patch<Rule>(`/api/v1/notifications/rules/${encodeURIComponent(id)}`, body),
      remove: (id: string) =>
        del<{ ok: boolean }>(`/api/v1/notifications/rules/${encodeURIComponent(id)}`),
    },
  },

  // ─── V3.1 fleet endpoints (per-market assignments / signals / journal) ─────
  fleet: {
    /** Bot-level cloud pause (the one the Pi enforces); resume reports anything broader still holding it. */
    pauseBot: (id: string, reason?: string) =>
      post<BotPauseResult>(`/api/v1/fleet/agents/${encodeURIComponent(id)}/pause`, reason ? { reason } : {}),
    resumeBot: (id: string) =>
      post<BotPauseResult>(`/api/v1/fleet/agents/${encodeURIComponent(id)}/resume`, {}),
    agentHealth: (id: string) =>
      get<AgentHealth>(`/api/v1/fleet/agents/${encodeURIComponent(id)}/health`),
    agentActions: (id: string, q: { limit?: number; cursor?: string; kind?: string } = {}) =>
      get<AgentActionPage>(`/api/v1/fleet/agents/${encodeURIComponent(id)}/actions${qs(q)}`),
    agentAssignments: (id: string) =>
      get<Assignment[]>(`/api/v1/fleet/agents/${encodeURIComponent(id)}/assignments`),
    assignments: (q: { market_id?: string } = {}) =>
      get<Assignment[]>(`/api/v1/fleet/assignments${qs(q)}`),
    /** Upsert assignments: apply one trading config to N agents for a market. */
    applyAssignments: (body: {
      market_id: string;
      config_version_id: string;
      agent_ids: string[];
      mode: "paper" | "live";
      relation?: "default" | "override";
      preview_hash?: string;
      expected_revisions?: Record<string, number>;
    }) => put<{ ok?: boolean; applied?: string[]; skipped_overrides?: string[]; applies_from?: string; revision_by_agent?: Record<string, number> }>("/api/v1/fleet/assignments", body),
    /** Pause execution; omit agent_ids for market-wide pause. */
    pauseAssignments: (body: { market_id: string; agent_ids?: string[] }) =>
      post<{ ok?: boolean }>("/api/v1/fleet/assignments/pause", body),
    /** Resume execution; omit agent_ids for market-wide resume. */
    resumeAssignments: (body: { market_id: string; agent_ids?: string[] }) =>
      post<{ ok?: boolean }>("/api/v1/fleet/assignments/resume", body),
    tradingConfigs: (q: { market_id?: string; limit?: number } = {}) =>
      get<TradingConfigVersion[] | { items: TradingConfigVersion[] }>(
        `/api/v1/fleet/configs/trading${qs(q)}`,
      ),
    signals: (
      q: {
        market_id?: string;
        agent_id?: string;
        verb?: SignalVerb;
        status?: DeliveryStatus;
        cursor?: string;
        limit?: number;
      } = {},
    ) => get<SignalPage>(`/api/v1/fleet/signals${qs(q)}`),
  },
};

// ─── Upgrade surfaces (catalog / controls / incidents / ops) ─────────────────

export interface CatalogCategory { id: string; venue_id: string | null; parent_id: string | null; label: string; slug: string; kind: string; sort_order: number; revision: number; archived_at: string | null }
export interface CatalogTree {
  categories: CatalogCategory[];
  product_families: Array<{ id: string; category_id: string; kind: string; label: string; legacy_market_id: string | null; window_seconds: number | null }>;
  competitions: Array<{ id: string; category_id: string | null; sport: string; label: string; venue_competition_id: string | null; venue_slug: string | null }>;
  subscriptions?: Array<{ id: string; category_id: string; label: string; kind: string; product_family_id: string | null; competition_id: string | null; status: string; health: Record<string, unknown>; revision: number; contract_types: string[]; follow_recurring: boolean }>;
  category_policies?: Array<{ scope_id: string; paused: boolean; reason: string | null; revision: string }>;
  tracked_instruments?: Array<{ product_family_id: string | null; competition_id: string | null; n: string }>;
}
export type PauseScopeKind = "fleet" | "venue" | "category" | "product_family" | "competition" | "subscription" | "event" | "instrument" | "bot" | "assignment";
export interface ControlPolicy { policy_id: string; scope_kind: PauseScopeKind; scope_id: string; paused: boolean; reason: string | null; actor: string; revision: number; updated_at: string }
export interface PauseStatusResponse {
  scope_kind: PauseScopeKind; scope_id: string; policy: ControlPolicy | null;
  accounts: Array<{ account_id: string; device_online: boolean; requested_revision: number; applied_revision: number | null; cancellations_pending: number; resting_orders: number; held_positions: number; states: string[] }>;
}
export interface Incident { id: string; severity: "info" | "warning" | "error" | "critical"; code: string; message: string; source?: string; correlation: Record<string, unknown>; status: "open" | "acknowledged" | "resolved"; occurrences: number; first_seen_at: string; last_seen_at: string; received_at: string }
export interface DeliveryRow {
  id: string; incident_id: string | null; channel_label: string | null; channel_type: string | null; event_kind: string; agent_id: string | null;
  status: string; attempts: number; max_attempts: number; next_attempt_at: string; last_error: string | null; created_at: string; sent_at: string | null;
  attempts_log: Array<{ attempt: number; started_at: string; finished_at: string | null; ok: boolean | null; http_status: number | null; error: string | null }> | null;
}
export interface OpsStatus {
  release: { server: string; shared: Record<string, unknown>; node: string };
  live_trading_armed_env: boolean;
  migrations: { pending: string[]; checksum_mismatches: string[]; applied: number; ledger_present: boolean };
  devices: Array<{ id: string; legacy_agent_id: string | null; display_name: string | null; release: string | null; protocol_version: number | null; last_seen_at: string | null; online: boolean; session: { epoch: number; ready: boolean; protocol_version: number } | null; lease_expires_at: string | null }>;
  bindings: Array<{ id: string; device_id: string; account_id: string; label: string; epoch: number; active: boolean; armed: boolean; armed_by: string | null; armed_at: string | null }>;
  backups: Array<{ id: number; kind: string; status: string; location: string | null; started_at: string; finished_at: string | null; detail: Record<string, unknown> }>;
  outbox: Array<{ status: string; n: number }>;
  deliveries: Array<{ status: string; n: number }>;
  open_incidents: Array<{ severity: string; n: number }>;
  database_size: string;
  readiness: Record<string, boolean>;
}

export const upgradeApi = {
  catalog: {
    tree: () => get<CatalogTree>("/api/v1/catalog/tree"),
    createCategory: (body: { parent_id: string; label: string; kind: string }) => post<CatalogCategory>("/api/v1/catalog/categories", body),
    updateCategory: (id: string, body: { label?: string; parent_id?: string; expected_revision?: number }) => patch<CatalogCategory>(`/api/v1/catalog/categories/${encodeURIComponent(id)}`, body),
    movePreview: (id: string, parentId: string) => get<Record<string, unknown>>(`/api/v1/catalog/categories/${encodeURIComponent(id)}/move-preview${qs({ parent_id: parentId })}`),
    archiveCategory: (id: string) => post<CatalogCategory>(`/api/v1/catalog/categories/${encodeURIComponent(id)}/archive`),
    competitions: (sport?: string) => get<Array<{ id: string; sport: string; label: string; venue_competition_id: string | null; editions: Array<{ id: string; label: string }> | null }>>(`/api/v1/catalog/competitions${qs({ sport })}`),
    createCompetition: (body: { sport: string; label: string; venue_competition_id: string; venue_slug?: string }) => post<{ id: string }>("/api/v1/catalog/competitions", body),
    subscriptions: () => get<NonNullable<CatalogTree["subscriptions"]>>("/api/v1/catalog/subscriptions"),
    createSubscription: (body: Record<string, unknown>) => post<Record<string, unknown>>("/api/v1/catalog/subscriptions", body),
    subscriptionStatusPreview: (id: string, status: string) => get<Record<string, unknown>>(`/api/v1/catalog/subscriptions/${encodeURIComponent(id)}/status-preview${qs({ status })}`),
    setSubscriptionStatus: (id: string, body: { status: string; remove_assignments?: boolean }) => post<Record<string, unknown>>(`/api/v1/catalog/subscriptions/${encodeURIComponent(id)}/status`, body),
    instruments: (q: Record<string, string | undefined> = {}) => get<Array<Record<string, unknown>>>(`/api/v1/catalog/instruments${qs(q)}`),
    coverage: () => get<{ subscriptions: Array<Record<string, unknown>>; gaps: Array<Record<string, unknown>>; instruments: Array<Record<string, unknown>>; archive: Record<string, unknown> }>("/api/v1/catalog/coverage"),
  },
  controls: {
    policies: () => get<ControlPolicy[]>("/api/v1/fleet/controls/policies"),
    pause: (body: { scope_kind: PauseScopeKind; scope_id: string; reason?: string }) => post<Record<string, unknown>>("/api/v1/fleet/controls/pause", body),
    resume: (body: { scope_kind: PauseScopeKind; scope_id: string }) => post<Record<string, unknown>>("/api/v1/fleet/controls/resume", body),
    status: (scope_kind: PauseScopeKind, scope_id: string) => get<PauseStatusResponse>(`/api/v1/fleet/controls/status${qs({ scope_kind, scope_id })}`),
    previewAssignments: (body: Record<string, unknown>) => post<{ targets: Array<Record<string, unknown>>; overrides_retained: Array<Record<string, unknown>>; preview_hash: string; applies_from: string }>("/api/v1/fleet/assignments/preview", body),
    positions: (agentId: string) => get<{ account_id: string; positions: Array<Record<string, unknown>>; legacy_open_orders: Array<Record<string, unknown>>; binding: Record<string, unknown> | null; session: Record<string, unknown> | null }>(`/api/v1/fleet/agents/${encodeURIComponent(agentId)}/positions`),
  },
  incidents: {
    list: (q: Record<string, string | undefined> = {}) => get<Incident[]>(`/api/v1/incidents${qs(q)}`),
    acknowledge: (id: string) => post<{ ok: boolean }>(`/api/v1/incidents/${encodeURIComponent(id)}/acknowledge`),
    resolve: (id: string, note?: string) => post<{ ok: boolean }>(`/api/v1/incidents/${encodeURIComponent(id)}/resolve`, { note }),
    deliveries: (q: Record<string, string | undefined> = {}) => get<DeliveryRow[]>(`/api/v1/incidents/deliveries${qs(q)}`),
    retryDelivery: (id: string) => post<{ ok: boolean }>(`/api/v1/incidents/deliveries/${encodeURIComponent(id)}/retry`),
  },
  ops: {
    status: () => get<OpsStatus>("/api/v1/ops/status"),
    arm: (accountId: string, body: { arm: boolean; confirm?: string }) => post<{ armed: boolean }>(`/api/v1/ops/bindings/${encodeURIComponent(accountId)}/arm`, body),
  },
};

/** Normalize the two accepted shapes of the trading-config catalogue. */
export function tradingConfigItems(
  data: TradingConfigVersion[] | { items: TradingConfigVersion[] } | undefined,
): TradingConfigVersion[] {
  if (!data) return [];
  return Array.isArray(data) ? data : data.items ?? [];
}
